import { useState } from "react";
import { Link } from "react-router-dom";
import { readWorkspace, writeWorkspace, datDauXoa } from "../lib/workspace.js";
import { track } from "../lib/track.js";
import {
  DOT, TRANG_THAI, GOI_Y_TOI_DA, sapXep, thongKe, nhacNho, capNhatMuc, boMuc,
} from "@shared/danhSachMucTieu.js";
import { fundTypeLabel, ticketText } from "@shared/fundDisplay.js";

/**
 * Danh sách quỹ mục tiêu trong Bản chuẩn bị (/ban-chuan-bi/nguon-von).
 *
 * Mỗi quỹ đã lưu có đợt tiếp cận, trạng thái, người giới thiệu và ghi chú. Dữ
 * liệu vẫn nằm trong workspace.saved nên đồng bộ tài khoản và sao lưu như cũ.
 * Cách làm theo bài Playbook "Bản đồ ba tầng gọi vốn": 15 đến 20 nơi, chia đợt,
 * luyện với nơi ít ưu tiên trước.
 */
export default function DanhSachMucTieu({ data, funds, tenCongTy, onDoi, anDau = false }) {
  const [moGhiChu, setMoGhiChu] = useState({});
  const [loi, setLoi] = useState("");
  const ds = data.saved || [];
  const theoId = new Map(funds.map((f) => [f.fundId, f]));
  const tk = thongKe(ds);
  const canhBao = nhacNho(ds, funds);

  function ghi(saved, them = {}) {
    try {
      writeWorkspace({ ...readWorkspace(), ...them, saved });
      onDoi?.();
      setLoi("");
    } catch {
      setLoi("Chưa lưu được. Kiểm tra bộ nhớ trình duyệt.");
    }
  }
  const sua = (id, patch) => {
    if (patch.trangThai) track("target_list_status", { status: patch.trangThai });
    ghi(capNhatMuc(readWorkspace().saved, id, patch));
  };
  const bo = (id) => {
    const d = readWorkspace();
    ghi(boMuc(d.saved, id), datDauXoa(d, `saved:${id}`));
  };

  async function xuatExcel() {
    try {
      const [{ dungTepDanhSach }, { taiVe }] = await Promise.all([
        import("@shared/excel/danhSachQuyBook.js"),
        import("../lib/xuatExcel.js"),
      ]);
      await taiVe(
        dungTepDanhSach(ds, funds, { tenDoanhNghiep: tenCongTy, ngayXuat: new Date().toISOString().slice(0, 10) }),
        "danh-sach-quy-muc-tieu",
      );
      track("target_list_export", { count: ds.length });
    } catch {
      setLoi("Chưa xuất được file. Thử lại hoặc dùng trình duyệt khác.");
    }
  }

  if (!ds.length) {
    return (
      <section className="surface section-space dsmt">
        <h2>Danh sách quỹ mục tiêu</h2>
        <p>
          Chưa có quỹ nào. Lọc theo giai đoạn và ngành trong công cụ tìm quỹ, rồi bấm
          “Thêm vào danh sách mục tiêu” ở những nơi hợp nhất. Nên chọn khoảng 15 đến 20 nơi.
        </p>
        <div className="hang-nut">
          <Link className="nut" to="/cong-cu/quy-phu-hop">Tìm quỹ phù hợp</Link>
          <Link className="nut phu" to="/nguon-von">Xem toàn bộ danh mục</Link>
        </div>
      </section>
    );
  }

  const nhom = [1, 2, 3].map((d) => [d, sapXep(ds).filter((x) => (x.dot || 2) === d)]);
  // Các bước tiếp cận theo thứ tự, để vẽ thanh tiến độ chung và chấm tiến độ từng quỹ.
  const BUOC = ["chua-tiep-can", "tim-gioi-thieu", "da-gui", "da-gap", "tham-dinh"];
  const NHAN_BUOC = { "chua-tiep-can": "Chưa tiếp cận", "tim-gioi-thieu": "Tìm giới thiệu", "da-gui": "Đã gửi", "da-gap": "Đã gặp", "tham-dinh": "Thẩm định" };
  const soDung = (tk.theoTrangThai["tu-choi"] || 0) + (tk.theoTrangThai["tam-dung"] || 0);
  return (
    <section className="dsmt mt">
      {!anDau && <div className="mt-dau">
        <h2>Quỹ mục tiêu</h2>
        <p className={`mt-dem ${tk.dangTheo > GOI_Y_TOI_DA ? "qua" : ""}`}>
          <strong>{tk.dangTheo}</strong>/{GOI_Y_TOI_DA} nơi đang theo đuổi
        </p>
      </div>}

      {/* Thanh tiến độ chung: bao nhiêu quỹ đang ở từng bước tiếp cận. */}
      <ol className={`mt-pheu${anDau ? " dau" : ""}`} aria-label="Tiến độ tiếp cận">
        {BUOC.map((b, i) => (
          <li key={b} className={(tk.theoTrangThai[b] || 0) > 0 ? "co" : ""}>
            <b>{tk.theoTrangThai[b] || 0}</b>
            <span>{i + 1}. {NHAN_BUOC[b]}</span>
          </li>
        ))}
      </ol>
      {soDung > 0 && <p className="mt-dung">{soDung} nơi đã từ chối hoặc tạm dừng.</p>}

      {canhBao.length > 0 && (
        <ul className="dsmt-nhac">
          {canhBao.map((c) => <li key={c}>{c}</li>)}
        </ul>
      )}

      {nhom.map(([d, muc]) => (
        <div key={d} className="mt-dot">
          <h3>{DOT[d].nhan} <span>{DOT[d].moTa} ({muc.length})</span></h3>
          {muc.length === 0 ? <p className="mt-trong">Chưa có nơi nào.</p> : (
            <ul className="mt-ds">
              {muc.map((x) => {
                const f = theoId.get(x.id);
                const tt = x.trangThai || "chua-tiep-can";
                const buoc = BUOC.indexOf(tt);
                const mo = moGhiChu[x.id];
                return (
                  <li key={x.id} className={`mt-muc tt-${tt}`}>
                    <div className="mt-hang">
                      <div className="mt-ten">
                        {f?.slug ? <Link to={`/nguon-von/${f.slug}`}>{x.name}</Link> : <span>{x.name}</span>}
                        {f && <small>{fundTypeLabel(f)}{ticketText(f) ? `, ${ticketText(f)}` : ""}</small>}
                        <span className={`mt-cham${buoc < 0 ? " dung" : ""}`} aria-hidden="true" title={TRANG_THAI[tt]}>
                          {BUOC.map((b, i) => <i key={b} className={buoc >= i ? "bat" : ""} />)}
                        </span>
                      </div>
                      <label className="mt-o">
                        <span className="sr-only">Đợt của {x.name}</span>
                        <select value={x.dot || 2} onChange={(e) => sua(x.id, { dot: Number(e.target.value) })}>
                          {[1, 2, 3].map((n) => <option key={n} value={n}>{DOT[n].nhan}</option>)}
                        </select>
                      </label>
                      <label className="mt-o mt-o-tt">
                        <span className="sr-only">Trạng thái của {x.name}</span>
                        <select value={tt} onChange={(e) => sua(x.id, { trangThai: e.target.value })}>
                          {Object.entries(TRANG_THAI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </label>
                      <button type="button" className="mt-mo" aria-expanded={!!mo}
                        onClick={() => setMoGhiChu((o) => ({ ...o, [x.id]: !o[x.id] }))}>
                        {mo ? "Thu gọn" : x.ghiChu || x.nguoiGioiThieu ? "Ghi chú ✎" : "Ghi chú"}
                      </button>
                    </div>
                    {mo && (
                      <div className="mt-chi-tiet">
                        <label>
                          <span>Người giới thiệu</span>
                          <input type="text" maxLength={120} defaultValue={x.nguoiGioiThieu || ""} placeholder="Ai có thể giới thiệu bạn"
                            onBlur={(e) => e.target.value.trim() !== (x.nguoiGioiThieu || "") && sua(x.id, { nguoiGioiThieu: e.target.value })} />
                        </label>
                        <label>
                          <span>Ghi chú</span>
                          <textarea rows="2" maxLength={500} defaultValue={x.ghiChu || ""}
                            placeholder="Vì sao nơi này phù hợp, đã trao đổi gì, bước tiếp theo"
                            onBlur={(e) => e.target.value.trim() !== (x.ghiChu || "") && sua(x.id, { ghiChu: e.target.value })} />
                        </label>
                        <button type="button" className="text-button danger" onClick={() => bo(x.id)}>Bỏ khỏi danh sách</button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ))}
      <p className="mt-ghi">
        Chia ba đợt: gặp vài nơi ít ưu tiên trước để luyện câu chuyện, sửa deck, rồi mới gặp nơi bạn muốn nhất.
        Xem <Link to="/playbook/ban-do-ba-tang-goi-von-viet-nam">Bản đồ ba tầng gọi vốn</Link>.
      </p>
      {loi && <p className="story-loi">{loi}</p>}
      <div className="hang-nut">
        <button type="button" className="nut" onClick={xuatExcel}>Xuất bảng theo dõi (.xlsx)</button>
        <Link className="nut phu" to="/cong-cu/quy-phu-hop">Tìm thêm quỹ</Link>
        <Link className="text-button" to="/playbook/email-tiep-can-quy-kem-vi-du">Cách viết email tiếp cận →</Link>
      </div>
      <p className="nho muted">
        File Excel có cột ngày cần nhắc lại tự tính và cùng cấu trúc với tab Danh sách quỹ của mẫu{" "}
        <Link to="/mau/theo-doi-tiep-can-nha-dau-tu">Bảng theo dõi tiếp cận nhà đầu tư</Link>.
      </p>
    </section>
  );
}
